import React, { useState } from "react";
import { Alert } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import {
  DRAWER_TEXT_MAX_CHARS,
  normalizeDrawerText,
} from "@components/SideDrawer/SideDrawer.schema";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useUploadBayHireAttachmentMetadata } from "@features/bay-hire/hooks/useBayHireMutations";
import {
  REASON_DIALOG_FIELDS as F,
  reasonDialogDefaultValues,
  reasonDialogSchema,
  type ReasonDialogFormData,
} from "@features/bay-hire/schema/reasonDialogSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { bayHireService, type StagedAttachment } from "@services/bayHireApi";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

// DFT-0044 slice F (F3): a single generic type shared by Reject and Cancel's optional
// supporting-document upload - see bay-hire-service's own BayHireRequestService for why one type
// serves both actions rather than two distinct ones.
const SUPPORTING_DOCUMENT_ATTACHMENT_TYPE = "SUPPORTING_DOCUMENT";

interface ReasonDialogProps {
  open: boolean;
  title: string;
  reasonLabel: string;
  submitLabel: string;
  testIdPrefix: string;
  /**
   * DFT-0044 slice F (F3): the request this reason applies to - needed only for the optional
   * supporting-document upload (`POST /bay-hire-requests/{id}/attachments/upload`), which is
   * scoped to an existing request unlike the create form's staged pre-request upload.
   */
  requestId: string;
  /**
   * DFT-0044 slice F (F3): opt-in, defaults to false. Reject/Cancel (both in the completeness
   * audit's F3 scope) pass true; RejectPaymentDialog does not - whether the payment-rejection
   * drawer also gains an upload depends on Q15, which the frontend completeness audit left
   * unanswered, so its screen stays unchanged rather than silently gaining a new field.
   */
  showAttachmentUpload?: boolean;
  onClose: () => void;
  /**
   * DFT-0044 slice F (F3): `attachmentId` is the id of a previously uploaded SUPPORTING_DOCUMENT
   * attachment (undefined if the vendor/Operations user chose not to attach one - it is optional
   * evidence, never a gate for the action).
   */
  onSubmit: (reason: string, attachmentId?: string) => Promise<void>;
}

/**
 * DFT-0010 (W-16, W-07): shared mandatory-reason panel used by both the
 * Reject (Operations) and Cancel (Operations or Vendor) flows.
 *
 * DFT-0027 (Slice A): migrated from hand-rolled useState + manual validation
 * onto react-hook-form + zod + the shared FormItem, consolidating the reason
 * bound onto the shared drawer-text helpers.
 *
 * DFT-0027 (Slice C): shell converted from the centered MUI Dialog to the
 * shared right-anchored SideDrawer (matches Figma W-13 and the app-wide
 * drawer convention). The external prop signature, form logic, schema, and
 * validation behaviour are unchanged - only the shell and footer buttons
 * change. `open` is mapped onto SideDrawer's `isOpen`; the panel's submit is
 * wired into SideDrawer's `onSubmit` via RHF `handleSubmit`.
 */
const ReasonDialog: React.FC<ReasonDialogProps> = ({
  open,
  title,
  reasonLabel,
  submitLabel,
  testIdPrefix,
  requestId,
  showAttachmentUpload = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  // DFT-0103: an optional supporting-document upload, staged on drop (blob-only, no backend
  // attach call) and registered as a durable per-request attachment only at submit time -
  // mirroring `BayHireCreateAttachmentUpload.tsx`'s own stage-then-register pattern. Previously
  // this called the eager `useUploadBayHireAttachment` hook, durably persisting the attachment the
  // instant a file was dropped - abandoning the drawer left an orphaned row behind (see task
  // DFT-0103).
  const [stagedAttachment, setStagedAttachment] = useState<StagedAttachment | null>(null);
  const [files, setFiles] = useState<UploadEntry[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isStaging, setIsStaging] = useState(false);
  const fileUploadProfiles = useFileUploadProfiles();
  const uploadConditions = fileUploadProfiles.bayHireAttachment;
  const registerAttachmentMutation = useUploadBayHireAttachmentMetadata();

  const form = useForm<ReasonDialogFormData>({
    resolver: zodResolver(reasonDialogSchema),
    mode: "onChange",
    defaultValues: reasonDialogDefaultValues,
  });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = form;

  // DFT-0096: keyed by attachment type (mirrors SingleCertificate.tsx's
  // useUploadStore key convention, not requestId/testIdPrefix) so a
  // staged-but-not-yet-submitted upload survives a refresh via
  // useUploadStore (FileUpload's own built-in integration - see its `name`
  // prop). Reject and Cancel share this same key, same as they already
  // share SUPPORTING_DOCUMENT_ATTACHMENT_TYPE server-side - only one of
  // this dialog's two uses is ever open at a time.
  const uploadStoreKey = SUPPORTING_DOCUMENT_ATTACHMENT_TYPE;

  const resetAttachment = () => {
    setStagedAttachment(null);
    setFiles([]);
    setUploadError(null);
    useUploadStore.getState().clearFilesForKey(uploadStoreKey);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset(reasonDialogDefaultValues);
    setSubmitError(null);
    resetAttachment();
    onClose();
  };

  // DFT-0103: stages the blob only (SAS direct-to-Azure, no backend attach call) - the durable
  // per-request registration is deferred to `handleSubmitForm`, matching
  // `BayHireCreateAttachmentUpload.tsx`'s own `handleUpload`.
  const handleUpload = async (selected: File[]) => {
    const file = selected[selected.length - 1];
    if (!file) return;

    setUploadError(null);
    setIsStaging(true);
    try {
      const staged = await bayHireService.uploadStagedAttachment(
        file,
        SUPPORTING_DOCUMENT_ATTACHMENT_TYPE,
      );
      setStagedAttachment(staged);
      // DFT-0096: overwrite FileUpload's own auto-synced entry (the raw,
      // not-meaningfully-serializable File object) with the real uploaded
      // reference, mirroring SingleCertificate.tsx's onSuccess - name/url,
      // not path/relativePath, is what should survive a refresh.
      useUploadStore.getState().setFilesForKey(uploadStoreKey, [
        {
          name: staged.originalFileName ?? file.name,
          url: staged.documentUrl ?? "",
          attachmentType: SUPPORTING_DOCUMENT_ATTACHMENT_TYPE,
        },
      ]);
    } catch {
      // The attachment stays out of `stagedAttachment` on failure - a failed upload must not
      // silently look like a successful, submittable one. Unlike the create form's HOD upload,
      // nothing here blocks submit either way: this is optional evidence, never a gate.
      setFiles([]);
      useUploadStore.getState().clearFilesForKey(uploadStoreKey);
      setUploadError(t("bay-hire.form.attachment-upload-failed"));
    } finally {
      setIsStaging(false);
    }
  };

  const handleRemove = () => {
    resetAttachment();
  };

  const handleSubmitForm = async (data: ReasonDialogFormData) => {
    setSubmitError(null);
    try {
      // DFT-0103: a freshly staged attachment has no `id` yet - register it durably against the
      // live request now, immediately before the real action call, exactly like
      // `useBayHireContainer.tsx`'s `handleUpdateFormSubmit`/`handleResubmitFormSubmit`.
      let attachmentId: string | undefined;
      if (stagedAttachment && !stagedAttachment.id) {
        const registered = await registerAttachmentMutation.mutateAsync({
          id: requestId,
          request: {
            attachmentType: stagedAttachment.attachmentType,
            documentUrl: stagedAttachment.documentUrl,
            originalFileName: stagedAttachment.originalFileName,
            contentType: stagedAttachment.contentType,
            fileSizeBytes: stagedAttachment.fileSizeBytes,
          },
        });
        attachmentId = registered.id;
      } else {
        attachmentId = stagedAttachment?.id ?? undefined;
      }

      await onSubmit(normalizeDrawerText(data.reason), attachmentId);
      reset(reasonDialogDefaultValues);
      resetAttachment();
    } catch (error) {
      console.error("Reason-dialog action failed:", error);
      // DFT-0066 (AD-6): prefer the server's own explanation over a generic retry prompt.
      //
      // Cancel is the reason this changed. It used to be fire-and-forget - the BFF returned 202
      // the moment the Temporal signal was accepted - so there was no failure detail to show and
      // "please try again" was all anyone could honestly say. Now the Vendor cancel is a Temporal
      // Update, so a refusal comes back as a 4xx ProblemDetails whose `detail` says WHY (e.g. the
      // workflow is no longer active), and telling the user to retry something that cannot
      // succeed is worse than useless.
      //
      // Applied to the shared dialog rather than special-cased for cancel: reject,
      // reject-payment and add-to-waiting-list all render through here and all benefit from
      // showing the real reason when the server sends one. Matches the platform convention
      // already used in gatepass (`RejectGatepassDrawer.tsx#L217`) and deficiencies.
      //
      // The generic message remains the fallback - a network error or an empty body has no
      // detail to show, and inventing one would be worse than the generic prompt.
      const problemDetail = (
        error as { response?: { data?: { detail?: string } }; data?: { detail?: string } }
      )?.response?.data?.detail;
      setSubmitError(problemDetail?.trim() || t("bay-hire.reason-submit-error"));
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-dialog`}>
      <SideDrawer
        isOpen={open}
        title={title}
        onClose={handleClose}
        onSubmit={() => void handleSubmit(handleSubmitForm)()}
        isSubmitting={isSubmitting}
        submitLabel={submitLabel}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
      >
        <div className="flex flex-col gap-2 px-6 py-2">
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid={`${testIdPrefix}-submit-error`}>
              {submitError}
            </Alert>
          )}
          <FormProvider {...form}>
            <FormItem<ReasonDialogFormData, typeof F.REASON>
              required
              name={F.REASON}
              type="textarea"
              label={reasonLabel}
              minRows={3}
              // DFT-0083: caps the textarea's own growth so it can never expand unbounded
              // internally regardless of content length/newlines - 12 rows comfortably fits a
              // full DRAWER_TEXT_MAX_CHARS-length reason before falling back to internal scroll.
              maxRows={12}
              maxChars={DRAWER_TEXT_MAX_CHARS}
              showCharCounter
              testId={`${testIdPrefix}-reason`}
            />
          </FormProvider>

          {/* DFT-0044 slice F (F3): 05_Port_License_details "Reject"/"Cancel" wireframes both show
              an Attachments drop zone below the reason - optional, never required. */}
          {showAttachmentUpload && (
            <div className="mt-2">
              <h4 className="text-sm text-gray-700 mb-2">{t("bay-hire.form.attachments")}</h4>

              {uploadError && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  data-testid={`${testIdPrefix}-attachment-upload-error`}
                >
                  {uploadError}
                </Alert>
              )}

              <FileUpload
                testId={`${testIdPrefix}-attachment-upload`}
                name={uploadStoreKey}
                onUpload={(selected) => void handleUpload(selected)}
                onRemoveFile={handleRemove}
                files={files}
                setFiles={setFiles}
                maxFiles={1}
                allowedExtensions={(uploadConditions.format ?? []).map((ext) => `.${ext}`)}
                conditions={uploadConditions}
                disabled={isSubmitting || isStaging}
                // DFT-0088 addendum (2026-09-01): matches the actual `bayHireAttachment`
                // allow-list - see FileUpload.constants.ts's own comment on that profile.
                uploadDescription={t("upload.dragOrDropDescription")}
              />

              {/* AC18/AC21 (DFT-0050): a visible remove control for the staged attachment -
                  `handleRemove` previously had no UI path that could ever fire it. Mirrors the
                  `SingleFile` row pattern used by every other multi-step upload in the app
                  (e.g. `PortServiceAdditionalDocuments.tsx`). */}
              {stagedAttachment && (
                <div className="mt-1" data-testid={`${testIdPrefix}-attachment-staged`}>
                  <SingleFile
                    testId={`${testIdPrefix}-attachment`}
                    file={
                      files[0] ?? {
                        name: stagedAttachment.originalFileName ?? stagedAttachment.documentUrl,
                      }
                    }
                    fieldName={`${testIdPrefix}-attachment-upload`}
                    setFiles={() => setFiles([])}
                    removeFn={handleRemove}
                    disabled={isSubmitting || isStaging}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default ReasonDialog;
